import React, { useMemo } from "react";

import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Link,
  makeStyles,
  Theme,
  Typography
} from "@material-ui/core";

import { MdAdd, MdCheckCircle, MdStar } from "react-icons/md";

import Can from "modules/user/config/permissions/Can";
import HStack from "modules/core/components/HStack";
import PremiumIcon from "modules/core/components/PremiumIcon";

import { useFeatureModulesQuery } from "modules/user/hooks/queries/useFeatureModulesQuery";
import { useAddAccountFeatureModuleMutation } from "modules/core/hooks/mutations/useAddAccountFeatureModuleMutation";

import { featureModuleContent } from "./constants";
import { sendTrackingEvent } from "modules/core/api/tracking";
import { trackingEvents } from "modules/user/config/tracking/events";

import { UserFeatureModuleKey } from "modules/user/types/entities";

interface IFeatureModuleFallbackModalProps {
  featureKey: UserFeatureModuleKey;
  isAbsolutePosition?: boolean;
  onClose?: () => void;
}

const useStyles = makeStyles<Theme, { isAbsolutePosition?: boolean }>(
  theme => ({
    backdrop: {
      position: ({ isAbsolutePosition }) =>
        isAbsolutePosition ? "absolute" : "fixed"
    },

    row: {
      alignItems: "stretch"
    },

    imageWrapper: {
      width: "30%",
      flexShrink: 0,
      flexGrow: 0
    },

    image: {
      width: "100%",
      height: "auto"
    },

    content: {
      display: "flex",
      flexDirection: "column",
      gap: 15,
      padding: "40px 40px 40px 20px",
      flexShrink: 1,
      flexGrow: 1,
      alignItems: "flex-start"
    },

    newChip: {
      backgroundColor: "#3FD4B1",
      color: theme.palette.common.white
    },

    list: {
      display: "flex",
      flexDirection: "column",
      gap: 10,
      marginBottom: 25
    },

    listIcon: {
      color: theme.palette.success.main,
      lineHeight: 0
    }
  })
);

const FeatureModuleFallbackModal: React.FC<
  IFeatureModuleFallbackModalProps
> = props => {
  const { featureKey, isAbsolutePosition, onClose } = props;

  const { data: featureModules } = useFeatureModulesQuery();
  const { mutate: addAccountFeatureModule, isLoading: isAddingFeatureModule } =
    useAddAccountFeatureModuleMutation();

  const module = featureModuleContent[featureKey];

  const classes = useStyles({ isAbsolutePosition });

  const isAvailable = useMemo(() => {
    if (!featureModules) return false;

    return featureModules.account_access.disabled.includes(featureKey);
  }, [featureModules, featureKey]);

  if (!module) return null;

  return (
    <Dialog
      open
      disablePortal={isAbsolutePosition}
      scroll="body"
      BackdropProps={{ classes: { root: classes.backdrop } }}
      style={{ position: isAbsolutePosition ? "absolute" : "fixed" }}
      onClose={onClose}
    >
      <HStack className={classes.row} gap={20}>
        <div className={classes.imageWrapper}>
          <img
            className={classes.image}
            src={module.image}
            alt={module.title}
          />
        </div>

        <div className={classes.content}>
          <div style={{ width: "100%" }}>
            <HStack
              style={{ justifyContent: "space-between", marginBottom: 7 }}
            >
              <HStack gap={7}>
                <PremiumIcon />

                <Typography
                  variant="caption"
                  color="primary"
                  style={{ fontWeight: 600 }}
                >
                  Premium Feature
                </Typography>
              </HStack>

              {module.isNew && (
                <Chip
                  icon={<MdStar />}
                  label="New"
                  size="small"
                  color="primary"
                  className={classes.newChip}
                />
              )}
            </HStack>

            <Typography variant="h6">{module.title}</Typography>
          </div>

          <Typography variant="body2" color="textSecondary">
            {module.description}
          </Typography>

          <div className={classes.list}>
            {module.listItems.map(item => (
              <HStack key={item} gap={7} style={{ alignItems: "flex-start" }}>
                <span className={classes.listIcon}>
                  <MdCheckCircle size={20} />
                </span>

                <Typography variant="body2" color="textSecondary">
                  {item}
                </Typography>
              </HStack>
            ))}
          </div>

          {isAvailable ? (
            <Can I="config" a="account" usingBestRole>
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  isAddingFeatureModule ? (
                    <CircularProgress size={14} />
                  ) : (
                    <MdAdd />
                  )
                }
                size="small"
                disabled={isAddingFeatureModule}
                onClick={() => {
                  addAccountFeatureModule(featureKey);
                }}
              >
                Add Module
              </Button>
            </Can>
          ) : (
            <>
              {onClose ? (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={onClose}
                >
                  Close
                </Button>
              ) : null}
            </>
          )}

          {featureModuleContent[featureKey]?.learnMoreLink ? (
            <Link
              href={featureModuleContent[featureKey]?.learnMoreLink}
              target="_blank"
              rel="noopener noreferrer"
              color="primary"
              variant="body2"
              style={{ marginBottom: 15 }}
              onClick={() => {
                sendTrackingEvent(
                  `${trackingEvents.feature_modules.clicked_learn_more}${featureModuleContent[featureKey]?.title}`,
                  {
                    title: "Learn More on PLG Screen"
                  }
                );
              }}
            >
              Learn More About {module.title}
            </Link>
          ) : null}
        </div>
      </HStack>
    </Dialog>
  );
};

export default React.memo(FeatureModuleFallbackModal);
